import React from "react";
import { ChevronDown, Hotel, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type HotelFilterOption = {
  id: string;
  name: string;
  logoId?: string | null;
  logoClassName: string;
  logoLetter: string;
};

type HotelFilterPopoverProps = {
  options: HotelFilterOption[];
  selectedHotels: string[];
  onApply: (hotels: string[]) => void;
};

export const HotelFilterPopover = ({
  options,
  selectedHotels,
  onApply,
}: HotelFilterPopoverProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [draftSelectedHotels, setDraftSelectedHotels] = React.useState<
    string[]
  >([]);

  const filteredOptions = React.useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return options;
    }
    return options.filter((option) =>
      option.name.toLowerCase().includes(normalizedSearch),
    );
  }, [options, search]);

  const toggleDraftHotel = (hotelId: string) => {
    setDraftSelectedHotels((prev) =>
      prev.includes(hotelId)
        ? prev.filter((value) => value !== hotelId)
        : [...prev, hotelId],
    );
  };

  const handleReset = () => {
    setSearch("");
    setDraftSelectedHotels([]);
    onApply([]);
    setOpen(false);
  };

  const handleApply = () => {
    onApply(draftSelectedHotels);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          setDraftSelectedHotels(selectedHotels);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="span"
          aria-label={t("billing.hotelFilter.hotel")}
          className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-sm font-medium text-zinc-900"
        >
          <Hotel className="size-4" />
          {t("billing.hotelFilter.hotel")}
          {selectedHotels.length > 0 ? ` (${selectedHotels.length})` : ""}
          <ChevronDown className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[320px] rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h4 className="text-base font-medium leading-normal text-[#171717]">
              {t("billing.dateFilter.filters")}
            </h4>
            <Button
              type="button"
              variant="span"
              className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
              onClick={() => setOpen(false)}
              aria-label={t("billing.dateFilter.closeFilters")}
            >
              <X className="size-5" />
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-base font-medium leading-normal text-zinc-600">
              {t("billing.hotelFilter.hotelBrand")}
              <ChevronDown className="size-4 rotate-180 text-zinc-500" />
            </div>

            <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-white shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10),0px_4px_6px_-1px_rgba(0,0,0,0.10)]">
              <div className="border-b border-[#E5E5E5] px-4 py-2">
                <div className="flex h-9 items-center gap-2 text-sm text-zinc-600">
                  <Search className="size-4 shrink-0 text-zinc-400" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("billing.hotelFilter.search")}
                    className="h-9 border-0 px-0 text-base leading-normal shadow-none focus-visible:ring-0"
                  />
                </div>
              </div>

              <div className="max-h-[176px] overflow-y-auto p-1">
                <div className="space-y-1">
                  {filteredOptions.map((option) => {
                    const isSelected = draftSelectedHotels.includes(option.id);
                    return (
                      <Button
                        key={option.id}
                        type="button"
                        variant="span"
                        aria-pressed={isSelected}
                        aria-label={option.name}
                        className={cn(
                          "flex min-h-8 w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-[#F5F5F5]",
                          isSelected && "bg-[#F5F5F5]",
                        )}
                        onClick={() => toggleDraftHotel(option.id)}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <PresignedImageWithPlaceholder
                            photo={option.logoId ?? undefined}
                            alt={option.name}
                            className="h-5 w-5 shrink-0 rounded-sm object-cover"
                            placeholder={
                              <span
                                className={cn(
                                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-[10px] font-semibold text-white",
                                  option.logoClassName || "bg-zinc-400",
                                )}
                              >
                                {option.logoLetter || option.name.charAt(0)}
                              </span>
                            }
                          />
                          <span className="truncate text-sm font-normal leading-normal text-[#171717]">
                            {option.name}
                          </span>
                        </span>
                        <Checkbox
                          checked={isSelected}
                          tabIndex={-1}
                          aria-hidden
                          className="pointer-events-none size-5 shrink-0 rounded-[4px] border-[#D5D7DA] data-[state=checked]:border-zinc-900 data-[state=checked]:bg-zinc-900"
                        />
                      </Button>
                    );
                  })}
                </div>
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-5 text-center text-sm text-zinc-500">
                    {t("billing.hotelFilter.noHotelsFound")}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-1">
            <Button
              type="button"
              variant="outline"
              className="h-8 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-sm font-medium leading-normal text-[#171717] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
              onClick={handleReset}
            >
              {t("billing.dateFilter.reset")}
            </Button>
            <Button
              type="button"
              className="h-8 w-fit rounded-md bg-[#6D82A3] px-4 py-2 text-sm font-medium leading-normal text-[#FAFAFA] hover:bg-[#5F7597]"
              onClick={handleApply}
            >
              {t("billing.dateFilter.applyFilters")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
